import { getAppConfig } from "@/config/app-config";
import { removeCookie } from "@/features/shared/cookies/cookie.store";
import { centralServerAxiosClient, removeCentralCipTokenHeader, setCentralCipTokenHeader } from "@/services/config/api";
import { CipUser } from "@EspaceParkQC/sdk-services-legacy";
import axios from "axios";
import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  Auth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  GoogleAuthProvider,
  initializeAuth,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
} from "firebase/auth";

/*
  Staff session (the staff identity bridge)
  =========================================

  The call center signs staff in to the clicknpark-staff Firebase project, its own session in this
  browser. Its ID token goes to the clicknpark API through sdk-call-center, in the header the CIP
  token used, and the API tells them apart. An open central-server session is handed off on load;
  when that fails, the call center keeps it as before.
*/

const STAFF_APP_NAME = "staff";

/** How long the handoff may take before the call center falls back to the central-server session. */
const HANDOFF_TIMEOUT_MS = 15_000;

let staffAuth: Auth | undefined;

function getStaffApp(): FirebaseApp {
  const existing = getApps().find((app) => app.name === STAFF_APP_NAME);
  if (existing) return existing;

  const config = getAppConfig();
  return initializeApp(
    {
      apiKey: config.VITE_STAFF_FIREBASE_API_KEY,
      authDomain: config.VITE_STAFF_FIREBASE_AUTH_DOMAIN,
      projectId: config.VITE_STAFF_FIREBASE_PROJECT_ID,
      appId: config.VITE_STAFF_FIREBASE_APP_ID,
    },
    STAFF_APP_NAME,
  );
}

function getStaffAuth(): Auth {
  if (staffAuth) return staffAuth;

  try {
    staffAuth = initializeAuth(getStaffApp(), {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    staffAuth = getAuth(getStaffApp());
  }

  return staffAuth;
}

/**
 * Whether a `cnp_cip_token` cookie holds a staff Firebase ID token rather than a central-server
 * session. Central writes its staff token under that same name on .clicknpark.com, where the call
 * center has always kept its central-server session: such a value is no session of ours.
 */
export function isStaffIdToken(token: string | undefined): boolean {
  const payload = token?.split(".")[1];
  if (!payload) return false;

  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { iss?: unknown };
    return typeof claims.iss === "string" && claims.iss.endsWith(`/${getAppConfig().VITE_STAFF_FIREBASE_PROJECT_ID}`);
  } catch {
    return false;
  }
}

/** The central-server session this page may hand off: the cookie, unless it holds Central's staff token. */
export function legacySessionToken(cookieValue: string | undefined): string | undefined {
  return cookieValue && !isStaffIdToken(cookieValue) ? cookieValue : undefined;
}

/**
 * Whether this page runs on a staff session: one Firebase restored, or an open central-server
 * session (the `cnp_cip_token` cookie, which holds central-server's session token here) handed off.
 */
export async function resolveStaffSession(cookieValue: string | undefined): Promise<boolean> {
  const auth = getStaffAuth();
  await auth.authStateReady();
  if (auth.currentUser) return true;

  const legacyToken = legacySessionToken(cookieValue);
  if (!legacyToken) return false;

  try {
    setCentralCipTokenHeader(legacyToken);
    const { data } = await centralServerAxiosClient.post<{ token: string }>("/v2/users/me/auth/staff/token", undefined, {
      timeout: HANDOFF_TIMEOUT_MS,
    });
    await signInWithCustomToken(auth, data.token);
  } catch (error) {
    console.warn("Staff handoff failed; staying on the central-server session.", error);
    return false;
  }

  removeCookie("cnp_cip_token");
  removeCookie("cnp_services_token");
  removeCentralCipTokenHeader();
  return true;
}

/** Google sign-in on the staff project. Its blocking function refuses anyone without staff access. */
export async function signInStaffWithGoogle(): Promise<void> {
  await signInWithPopup(getStaffAuth(), new GoogleAuthProvider());
}

/** Signs out of the staff project. Awaited before leaving, or the saved session would sign right back in. */
export async function signOutOfStaff(): Promise<void> {
  await signOut(getStaffAuth());
}

/**
 * The signed-in staff member's ID token and when they last signed in, or undefined when nobody is.
 * Firebase hands back the token it holds and renews it first when it is about to expire (within 30
 * seconds; it lives an hour), so this is read before each request rather than kept.
 */
export async function getStaffIdToken(): Promise<{ token: string; signedInAt: Date } | undefined> {
  const user = getStaffAuth().currentUser;
  if (!user) return undefined;

  const { token, authTime } = await user.getIdTokenResult();
  return { token, signedInAt: new Date(authTime) };
}

/** `GET /staff/me` on the clicknpark API, in the shape the call center has always used for its user. */
export async function getStaffUser(token: string): Promise<CipUser> {
  const { data } = await axios.get<{
    uid: string;
    email: string;
    firstName: string;
    lastName: string;
    sipInfo: CipUser["sipInfo"] | null;
  }>(`${getAppConfig().VITE_CLICKNPARK_API_URL}/staff/me`, { headers: { "X-Staff-Auth": token } });

  return {
    id: data.uid,
    username: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    fullName: `${data.firstName} ${data.lastName}`.trim(),
    sipInfo: data.sipInfo ?? { identity: "", username: "", password: "", registrar: "" },
  } as CipUser;
}
